import type { CanvasNode, ViewportTransform } from '../types';

/**
 * Where the view is on the canvas, in canvas units: what the minimap draws and what "show
 * all nodes" aims for. A transform maps a canvas point p to the screen as p * scale + (x, y).
 */

export interface Bounds {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
}

export const MIN_SCALE = 0.1;
export const MAX_SCALE = 5;
/** Nodes without a stored size are drawn this big (as the canvas lays them out). */
const DEFAULT_NODE_SIZE = { width: 320, height: 200 };

export const nodeRect = (node: Pick<CanvasNode, 'position' | 'size'>) => ({
    x: node.position.x,
    y: node.position.y,
    width: node.size?.width || DEFAULT_NODE_SIZE.width,
    height: node.size?.height || DEFAULT_NODE_SIZE.height,
});

/** The box around all the nodes, or null when there are none. */
export const nodesBounds = (nodes: Pick<CanvasNode, 'position' | 'size'>[]): Bounds | null => {
    if (nodes.length === 0) return null;
    const bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };
    for (const node of nodes) {
        const r = nodeRect(node);
        bounds.minX = Math.min(bounds.minX, r.x);
        bounds.minY = Math.min(bounds.minY, r.y);
        bounds.maxX = Math.max(bounds.maxX, r.x + r.width);
        bounds.maxY = Math.max(bounds.maxY, r.y + r.height);
    }
    return bounds;
};

/** The part of the canvas on screen. */
export const viewBounds = (t: ViewportTransform, width: number, height: number): Bounds => ({
    minX: (0 - t.x) / t.scale,
    minY: (0 - t.y) / t.scale,
    maxX: (width - t.x) / t.scale,
    maxY: (height - t.y) / t.scale,
});

export const unionBounds = (a: Bounds | null, b: Bounds): Bounds => (a
    ? { minX: Math.min(a.minX, b.minX), minY: Math.min(a.minY, b.minY), maxX: Math.max(a.maxX, b.maxX), maxY: Math.max(a.maxY, b.maxY) }
    : b);

export const overlaps = (a: Bounds, b: Bounds): boolean =>
    a.minX < b.maxX && b.minX < a.maxX && a.minY < b.maxY && b.minY < a.maxY;

const clampScale = (scale: number) => Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));

/** The view with canvas point (cx, cy) in the middle of the screen. */
export const centeredOn = (cx: number, cy: number, scale: number, width: number, height: number): ViewportTransform => ({
    x: width / 2 - cx * scale,
    y: height / 2 - cy * scale,
    scale,
});

export interface Insets {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

/**
 * The view that shows all of `bounds` with `padding` screen pixels around it, or more on the
 * sides given in `insets` (where controls sit over the canvas). It never zooms in past
 * `maxScale`, so a single small node is shown at its real size, not blown up.
 */
export const fitBounds = (
    bounds: Bounds,
    width: number,
    height: number,
    { padding = 80, maxScale = 1, insets }: { padding?: number; maxScale?: number; insets?: Partial<Insets> } = {},
): ViewportTransform => {
    const pad: Insets = { top: padding, right: padding, bottom: padding, left: padding, ...insets };
    const areaWidth = Math.max(1, width - pad.left - pad.right);
    const areaHeight = Math.max(1, height - pad.top - pad.bottom);
    const w = Math.max(1, bounds.maxX - bounds.minX);
    const h = Math.max(1, bounds.maxY - bounds.minY);
    const scale = clampScale(Math.min(maxScale, areaWidth / w, areaHeight / h));
    // Centred in the free area, not the whole screen
    return {
        x: pad.left + areaWidth / 2 - (bounds.minX + w / 2) * scale,
        y: pad.top + areaHeight / 2 - (bounds.minY + h / 2) * scale,
        scale,
    };
};

/** The same view at another zoom, keeping the middle of the screen where it is. */
export const zoomedAtCenter = (t: ViewportTransform, scale: number, width: number, height: number): ViewportTransform => {
    const next = clampScale(scale);
    const cx = (width / 2 - t.x) / t.scale;
    const cy = (height / 2 - t.y) / t.scale;
    return centeredOn(cx, cy, next, width, height);
};

export interface MapFrame {
    /** Minimap pixels per canvas unit. */
    scale: number;
    /** Where the frame's canvas box starts, so a canvas point lands at (x - minX) * scale + offsetX. */
    minX: number;
    minY: number;
    offsetX: number;
    offsetY: number;
}

/** How a canvas box is drawn in a minimap of the given size: whole, in the middle, with a margin. */
export const mapFrame = (bounds: Bounds, boxWidth: number, boxHeight: number, margin = 8): MapFrame => {
    const w = Math.max(1, bounds.maxX - bounds.minX);
    const h = Math.max(1, bounds.maxY - bounds.minY);
    const scale = Math.min((boxWidth - margin * 2) / w, (boxHeight - margin * 2) / h);
    return {
        scale,
        minX: bounds.minX,
        minY: bounds.minY,
        offsetX: (boxWidth - w * scale) / 2,
        offsetY: (boxHeight - h * scale) / 2,
    };
};

export const toMap = (frame: MapFrame, x: number, y: number) => ({
    x: (x - frame.minX) * frame.scale + frame.offsetX,
    y: (y - frame.minY) * frame.scale + frame.offsetY,
});

export const fromMap = (frame: MapFrame, mx: number, my: number) => ({
    x: (mx - frame.offsetX) / frame.scale + frame.minX,
    y: (my - frame.offsetY) / frame.scale + frame.minY,
});
